import { lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from '../contexts/AuthContext';
import { PageLoader } from '../components/common/PageLoader';
import { ErrorBoundary } from '../components/common/ErrorBoundary';
import { getAppUrl } from '../utils/domain';

// Eagerly loaded (frequently accessed)
import PublicLanding from '../pages/PublicLanding';
import Search from '../pages/Search';
import Memorial from '../pages/Memorial';
import Welcome from '../pages/Welcome';
import Activate from '../pages/Activate';
import ShortUrlRedirect from '../components/ShortUrlRedirect';

// Lazy loaded (less frequently accessed)
const CancelOrder = lazy(() => import('../pages/CancelOrder'));
const LeSueurLanding = lazy(() => import('../pages/LeSueurLanding'));
const WrightLanding = lazy(() => import('../pages/WrightLanding'));
const Partners = lazy(() => import('../pages/Partners'));
const About = lazy(() => import('../pages/About'));
const Contact = lazy(() => import('../pages/Contact'));
const PrivacyPolicy = lazy(() => import('../pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('../pages/TermsOfService'));

/**
 * Redirects unknown routes to the app domain, preserving path and query params
 */
const RedirectToApp = () => {
  const location = useLocation();
  const fullPath = location.pathname + location.search + location.hash;
  window.location.href = getAppUrl(fullPath);
  return null;
};

/**
 * Public Router for pontis.life domain
 * Serves public-facing pages without authentication
 */
const PublicRouter = () => {
  return (
    <AuthProvider>
    <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Public landing and search */}
      <Route path="/" element={<PublicLanding />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/search" element={<Search />} />
      <Route path="/partners" element={<Partners />} />
      
      {/* Memorial viewing (public) */}
      <Route path="/memorial/:id" element={<Memorial />} />
      
      {/* Short URL redirect for compact QR codes */}
      <Route path="/m/:code" element={<ShortUrlRedirect />} />
      
      {/* Welcome and activation flows */}
      <Route path="/welcome/:id" element={<Welcome />} />
      <Route path="/activate/:id" element={<Activate />} />
      
      {/* Order cancellation */}
      <Route path="/cancel-order" element={<CancelOrder />} />
      
      {/* Partner landing pages */}
      <Route path="/LeSueur" element={<LeSueurLanding />} />
      <Route path="/Wright" element={<WrightLanding />} />
      
      {/* Legal pages */}
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      
      {/* Catch-all: redirect to app domain */}
      <Route path="*" element={<RedirectToApp />} />
    </Routes>
    </Suspense>
    </ErrorBoundary>
    </AuthProvider>
  );
};

export default PublicRouter;
